import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ArrowLeft, ArrowDown, Bell, Check, CheckCheck, CornerUpLeft, Download, FileText, Link2,
  LogOut, MessageCircle, Mic, Paperclip, Plus, Search, Send, Settings2, Trash2, Users as UsersIcon, X,
  ShoppingCart, Factory, Truck, FileBox,
} from 'lucide-react';
import { chatAPI } from '../utils/api';
import useAuthStore from '../store/authStore';
import useChatStore from '../store/chatStore';
import VoicePlayer, { fmtDur } from '../components/chat/VoicePlayer';
import useVoiceRecorder, { MAX_VOICE_SECONDS } from '../components/chat/useVoiceRecorder';

// ---------------------------------------------------------------- small helpers
const PALETTE = ['#2dd4bf', '#818cf8', '#f472b6', '#fbbf24', '#34d399', '#38bdf8', '#a78bfa', '#fb923c'];
const colorFor = (id) => PALETTE[[...String(id || '')].reduce((a, c) => a + c.charCodeAt(0), 0) % PALETTE.length];
const initials = (name) => (name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('');
const REF_META = {
  sale: { icon: ShoppingCart, path: '/sales', key: 'doc_sale' },
  lading_bill: { icon: FileBox, path: '/lading-bills', key: 'doc_lading_bill' },
  production: { icon: Factory, path: '/production', key: 'doc_production' },
  purchase: { icon: Truck, path: '/purchases', key: 'doc_purchase' },
};
const fmtSize = (b) => (b == null ? '' : b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1048576).toFixed(1)} MB`);
const isTouch = () => window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

function useLocale() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  return {
    lang,
    time: (iso) => new Date(iso).toLocaleTimeString(lang === 'fa' ? 'fa-IR' : lang === 'ru' ? 'ru-RU' : 'en-GB', { hour: '2-digit', minute: '2-digit' }),
    day: (iso) => new Date(iso).toLocaleDateString(lang === 'fa' ? 'fa-IR-u-ca-persian' : lang === 'ru' ? 'ru-RU' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long' }),
    short: (iso) => new Date(iso).toLocaleDateString(lang === 'fa' ? 'fa-IR-u-ca-persian' : lang === 'ru' ? 'ru-RU' : 'en-GB', { day: 'numeric', month: 'short' }),
  };
}

const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();

function Avatar({ id, name, group, size = 40 }) {
  return (
    <span className="chat-avatar" style={{ '--c': colorFor(id), width: size, height: size, fontSize: size * 0.36 }}>
      {group ? <UsersIcon size={size * 0.45} /> : initials(name)}
    </span>
  );
}

function preview(t, m, meId) {
  if (!m) return '';
  if (m.deleted) return t('message_deleted');
  const who = m.sender_id === meId ? t('you') + ': ' : '';
  if (m.kind === 'voice') return `${who}🎤 ${t('voice_message')} ${fmtDur(m.duration)}`;
  if (m.kind === 'image') return `${who}🖼 ${m.body || t('photo')}`;
  if (m.kind === 'file') return `${who}📎 ${m.file_name || t('file')}`;
  return who + (m.body || '');
}

// ---------------------------------------------------------------- new chat / group modal
function NewChatModal({ t, onClose, onCreated }) {
  const [users, setUsers] = useState(null);
  const [mode, setMode] = useState('direct');
  const [picked, setPicked] = useState([]);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { chatAPI.users().then(r => setUsers(r.data)).catch(() => setUsers([])); }, []);

  const create = async (ids, groupTitle) => {
    setBusy(true);
    try {
      const r = await chatAPI.create({ user_ids: ids, title: groupTitle || null });
      onCreated(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || t('error')); }
    setBusy(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal chat-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{mode === 'direct' ? t('new_chat') : t('new_group')}</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="chat-seg">
            <button className={mode === 'direct' ? 'on' : ''} onClick={() => setMode('direct')}><MessageCircle size={14} /> {t('new_chat')}</button>
            <button className={mode === 'group' ? 'on' : ''} onClick={() => setMode('group')}><UsersIcon size={14} /> {t('new_group')}</button>
          </div>
          {mode === 'group' && (
            <input className="input" style={{ marginTop: 12 }} placeholder={t('group_name')} value={title} onChange={e => setTitle(e.target.value)} maxLength={100} />
          )}
          <div className="chat-people">
            {users === null ? <div className="loading-center"><div className="spinner" /></div>
              : users.length === 0 ? <p className="chat-empty-note">{t('no_other_users')}</p>
              : users.map(u => {
                const on = picked.includes(u.id);
                return (
                  <button key={u.id} className={`chat-person ${on ? 'on' : ''}`} disabled={busy}
                    onClick={() => mode === 'direct' ? create([u.id]) : setPicked(on ? picked.filter(x => x !== u.id) : [...picked, u.id])}>
                    <Avatar id={u.id} name={u.full_name} size={36} />
                    <span className="chat-person-name">{u.full_name}<small>{t('role_' + u.role)}</small></span>
                    {mode === 'group' && <span className="chat-check">{on && <Check size={14} />}</span>}
                  </button>
                );
              })}
          </div>
        </div>
        {mode === 'group' && (
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>{t('cancel')}</button>
            <button className="btn btn-primary" disabled={busy || !title.trim() || picked.length === 0} onClick={() => create(picked, title.trim())}>
              <Plus size={15} /> {t('create_group')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- group settings
function GroupModal({ t, conv, me, onClose, onSaved, onLeft }) {
  const [users, setUsers] = useState([]);
  const [title, setTitle] = useState(conv.title);
  const [members, setMembers] = useState(conv.members.map(m => m.id));
  const canManage = conv.created_by === me.id || useAuthStore.getState().can('users.manage');
  useEffect(() => { chatAPI.users().then(r => setUsers(r.data)).catch(() => {}); }, []);
  const all = [{ id: me.id, full_name: me.full_name, role: me.role }, ...users];
  const save = async () => {
    const before = conv.members.map(m => m.id);
    try {
      const r = await chatAPI.update(conv.id, {
        title, add_user_ids: members.filter(id => !before.includes(id)), remove_user_ids: before.filter(id => !members.includes(id)),
      });
      onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || t('error')); }
  };
  const leave = async () => {
    if (!window.confirm(t('confirm_leave'))) return;
    try { await chatAPI.leave(conv.id); onLeft(conv.id); } catch (e) { toast.error(e.response?.data?.detail || t('error')); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal chat-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>{t('group_info')}</h3><button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button></div>
        <div className="modal-body">
          <label className="form-label">{t('group_name')}</label>
          <input className="input" value={title} disabled={!canManage} onChange={e => setTitle(e.target.value)} maxLength={100} />
          <label className="form-label" style={{ marginTop: 14 }}>{t('members')}</label>
          <div className="chat-people">
            {all.map(u => {
              const on = members.includes(u.id);
              return (
                <button key={u.id} className={`chat-person ${on ? 'on' : ''}`} disabled={!canManage || u.id === me.id}
                  onClick={() => setMembers(on ? members.filter(x => x !== u.id) : [...members, u.id])}>
                  <Avatar id={u.id} name={u.full_name} size={34} />
                  <span className="chat-person-name">{u.full_name}{u.id === me.id && <small>{t('you')}</small>}</span>
                  <span className="chat-check">{on && <Check size={14} />}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-danger" onClick={leave}><LogOut size={15} /> {t('leave_group')}</button>
          {canManage && <button className="btn btn-primary" onClick={save}>{t('save')}</button>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- link an ERP document
function RefPicker({ t, onPick, onClose }) {
  const [type, setType] = useState('sale');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    const h = setTimeout(() => chatAPI.refs(type, q).then(r => setRows(r.data)).catch(() => setRows([])), 220);
    return () => clearTimeout(h);
  }, [type, q]);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal chat-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>{t('attach_document')}</h3><button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button></div>
        <div className="modal-body">
          <div className="chat-seg four">
            {Object.entries(REF_META).map(([k, m]) => {
              const Icon = m.icon;
              return <button key={k} className={type === k ? 'on' : ''} onClick={() => setType(k)}><Icon size={14} /> {t(m.key)}</button>;
            })}
          </div>
          <div className="chat-search" style={{ margin: '12px 0' }}>
            <Search size={14} />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('search_documents')} autoFocus={!isTouch()} />
          </div>
          <div className="chat-people">
            {rows === null ? <div className="loading-center"><div className="spinner" /></div>
              : rows.length === 0 ? <p className="chat-empty-note">{t('no_results')}</p>
              : rows.map(r => (
                <button key={r.id} className="chat-person" onClick={() => onPick({ type, id: r.id, label: r.label })}>
                  <span className="chat-ref-ico">{React.createElement(REF_META[type].icon, { size: 16 })}</span>
                  <span className="chat-person-name"><bdi>{r.label}</bdi><small><bdi>{r.sub}</bdi></small></span>
                </button>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- one message
function Bubble({ t, m, mine, showName, readUpto, isGroup, canDelete, onReply, onDelete, onJump, onImage, loc, selected, onSelect }) {
  const navigate = useNavigate();
  const seen = mine && readUpto >= m.id;
  const Ref = m.ref ? REF_META[m.ref.type] : null;
  return (
    <div className={`msg-row ${mine ? 'mine' : ''} ${selected ? 'selected' : ''}`} id={`msg-${m.id}`}>
      <div className={`msg ${m.kind} ${m.deleted ? 'deleted' : ''}`} onClick={() => onSelect(m.id)}>
        {showName && isGroup && !mine && <div className="msg-name" style={{ color: colorFor(m.sender_id) }}>{m.sender_name}</div>}
        {m.deleted ? <div className="msg-text"><em>{t('message_deleted')}</em></div> : (
          <>
            {m.reply_to && (
              <button type="button" className="msg-quote" onClick={e => { e.stopPropagation(); onJump(m.reply_to.id); }}>
                <b>{m.reply_to.sender_name}</b>
                <span>{m.reply_to.deleted ? t('message_deleted') : m.reply_to.kind === 'voice' ? '🎤 ' + t('voice_message') : m.reply_to.body || t(m.reply_to.kind === 'image' ? 'photo' : 'file')}</span>
              </button>
            )}
            {Ref && (
              <button type="button" className="msg-ref" onClick={e => { e.stopPropagation(); navigate(Ref.path); }}>
                <Ref.icon size={14} /><span>{t(Ref.key)}</span><bdi>{m.ref.label}</bdi>
              </button>
            )}
            {m.kind === 'voice' && <VoicePlayer id={m.id} src={m.file_url} duration={m.duration} waveform={m.waveform} mine={mine} />}
            {m.kind === 'image' && (
              <button type="button" className="msg-image" onClick={e => { e.stopPropagation(); onImage(m.file_url); }}>
                <img src={m.file_url} alt={m.file_name || ''} loading="lazy" />
              </button>
            )}
            {m.kind === 'file' && (
              <a className="msg-file" href={m.file_url} download={m.file_name} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
                <span className="msg-file-ico"><FileText size={18} /></span>
                <span className="msg-file-name"><bdi>{m.file_name}</bdi><small><bdi dir="ltr">{fmtSize(m.file_size)}</bdi></small></span>
                <Download size={15} />
              </a>
            )}
            {m.body && <div className="msg-text" dir="auto">{m.body}</div>}
          </>
        )}
        <div className="msg-meta">
          <span>{loc.time(m.created_at)}</span>
          {mine && !m.deleted && (seen ? <CheckCheck size={14} className="seen" aria-label={t('seen')} /> : <Check size={14} aria-label={t('sent')} />)}
        </div>
      </div>
      {!m.deleted && (
        <div className="msg-actions">
          <button type="button" onClick={() => onReply(m)} title={t('reply')}><CornerUpLeft size={14} /></button>
          {canDelete && <button type="button" onClick={() => onDelete(m)} title={t('delete_message')} className="danger"><Trash2 size={14} /></button>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- composer
function Composer({ t, conv, replyTo, setReplyTo, onSent }) {
  const [text, setText] = useState('');
  const [ref, setRef] = useState(null);
  const [showRef, setShowRef] = useState(false);
  const [upload, setUpload] = useState(null);   // {name, pct}
  const fileInput = useRef(null);
  const area = useRef(null);
  const lastTyping = useRef(0);
  const rec = useVoiceRecorder();
  const stopping = useRef(false);

  useEffect(() => { setText(''); setRef(null); }, [conv.id]);
  useEffect(() => { if (replyTo && area.current && !isTouch()) area.current.focus(); }, [replyTo]);
  useLayoutEffect(() => {
    const el = area.current; if (!el) return;
    el.style.height = 'auto'; el.style.height = Math.min(140, el.scrollHeight) + 'px';
  }, [text]);

  const post = async (fd, label) => {
    if (replyTo) fd.append('reply_to_id', replyTo.id);
    if (ref) { fd.append('ref_type', ref.type); fd.append('ref_id', ref.id); fd.append('ref_label', ref.label); }
    if (label) setUpload({ name: label, pct: 0 });
    try {
      const r = await chatAPI.send(conv.id, fd, label ? (e) => setUpload({ name: label, pct: e.total ? Math.round((e.loaded / e.total) * 100) : 0 }) : undefined);
      onSent(r.data);
      setReplyTo(null); setRef(null);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || t('send_failed'));
      return false;
    } finally { setUpload(null); }
  };

  const sendText = async () => {
    const body = text.trim();
    if (!body) return;
    const fd = new FormData(); fd.append('body', body);
    setText('');
    const ok = await post(fd);
    if (!ok) setText(body);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) { toast.error(t('file_too_large')); return; }
    const fd = new FormData(); fd.append('file', f);
    if (text.trim()) fd.append('body', text.trim());
    const ok = await post(fd, f.name);
    if (ok) setText('');
  };

  const startRec = async () => {
    try {
      stopping.current = false;
      await rec.start(() => { if (!stopping.current) { stopping.current = true; toast(t('voice_limit')); finishRec(); } });
    } catch (e) {
      const k = { insecure: 'mic_need_https', unsupported: 'mic_unsupported', denied: 'mic_denied', nodevice: 'mic_no_device' }[e.message] || 'mic_denied';
      toast.error(t(k), { duration: 6000 });
    }
  };
  const finishRec = async () => {
    stopping.current = true;
    const res = await rec.stop();
    if (!res) return;
    const ext = res.mime.includes('mp4') ? 'm4a' : res.mime.includes('ogg') ? 'ogg' : 'webm';
    const fd = new FormData();
    fd.append('kind', 'voice');
    fd.append('file', res.blob, `voice.${ext}`);
    fd.append('duration', Math.min(MAX_VOICE_SECONDS, res.duration).toFixed(1));
    fd.append('waveform', JSON.stringify(res.waveform));
    await post(fd, t('voice_message'));
  };

  const onType = (v) => {
    setText(v);
    const now = Date.now();
    if (v && now - lastTyping.current > 3000) { lastTyping.current = now; chatAPI.typing(conv.id).catch(() => {}); }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isTouch() && !e.nativeEvent.isComposing) { e.preventDefault(); sendText(); }
  };

  const recording = rec.state === 'recording';
  return (
    <div className="composer-wrap">
      {(replyTo || ref) && (
        <div className="composer-context">
          {replyTo && (
            <div className="ctx-chip">
              <CornerUpLeft size={14} />
              <span><b>{replyTo.sender_name}</b> {replyTo.kind === 'voice' ? '🎤 ' + t('voice_message') : (replyTo.body || replyTo.file_name || '').slice(0, 80)}</span>
              <button type="button" onClick={() => setReplyTo(null)}><X size={14} /></button>
            </div>
          )}
          {ref && (
            <div className="ctx-chip ref">
              {React.createElement(REF_META[ref.type].icon, { size: 14 })}
              <span>{t(REF_META[ref.type].key)} <bdi>{ref.label}</bdi></span>
              <button type="button" onClick={() => setRef(null)}><X size={14} /></button>
            </div>
          )}
        </div>
      )}
      {upload && <div className="composer-upload"><span>{upload.name}</span><i style={{ width: upload.pct + '%' }} /></div>}
      {recording ? (
        <div className="composer recording">
          <button type="button" className="cmp-btn" onClick={() => rec.cancel()} title={t('cancel')}><Trash2 size={18} /></button>
          <div className="rec-live">
            <span className="rec-dot" />
            <span className="rec-time">{fmtDur(rec.elapsed)}</span>
            <div className="rec-meter">
              {Array.from({ length: 28 }, (_, i) => {
                const v = rec.levels[rec.levels.length - 28 + i];
                return <i key={i} style={{ transform: `scaleY(${v == null ? 0.08 : Math.max(0.08, v)})` }} />;
              })}
            </div>
            <span className="rec-left">{fmtDur(MAX_VOICE_SECONDS - rec.elapsed)}</span>
          </div>
          <button type="button" className="cmp-send" onClick={finishRec} title={t('send')}><Send size={18} /></button>
        </div>
      ) : (
        <div className="composer">
          <button type="button" className="cmp-btn" onClick={() => fileInput.current?.click()} title={t('attach_file')} disabled={!!upload}><Paperclip size={18} /></button>
          <button type="button" className="cmp-btn" onClick={() => setShowRef(true)} title={t('attach_document')}><Link2 size={18} /></button>
          <textarea ref={area} rows={1} value={text} onChange={e => onType(e.target.value)} onKeyDown={onKey}
            placeholder={t('type_message')} dir="auto" maxLength={4000} />
          {text.trim()
            ? <button type="button" className="cmp-send" onClick={sendText} title={t('send')}><Send size={18} /></button>
            : <button type="button" className="cmp-send mic" onClick={startRec} title={t('record_voice')} disabled={!!upload}><Mic size={19} /></button>}
          <input ref={fileInput} type="file" hidden onChange={onFile}
            accept="image/*,.pdf,.xlsx,.xls,.docx,.doc,.txt,.csv,.zip,.mp3,.m4a,.ogg,.mp4" />
        </div>
      )}
      {showRef && <RefPicker t={t} onClose={() => setShowRef(false)} onPick={(r) => { setRef(r); setShowRef(false); }} />}
    </div>
  );
}

// ---------------------------------------------------------------- page
export default function Chat() {
  const { t } = useTranslation();
  const loc = useLocale();
  const { user: me } = useAuthStore();
  const [params, setParams] = useSearchParams();
  const store = useChatStore();
  const { conversations, messages, hasMore, unread, typing, readUpto } = store;
  const activeId = params.get('c') ? +params.get('c') : null;
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showGroup, setShowGroup] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [selected, setSelected] = useState(null);
  const [atBottom, setAtBottom] = useState(true);
  const [notifPerm, setNotifPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const scroller = useRef(null);
  const loadingOlder = useRef(false);

  const conv = conversations.find(c => c.id === activeId) || null;
  const list = messages[activeId] || null;

  useEffect(() => {
    store.setOnChatPage(true);
    store.loadConversations().finally(() => setLoaded(true));
    return () => { store.setOnChatPage(false); store.setActive(null); document.body.classList.remove('chat-thread-open'); };
  }, []);

  useEffect(() => {
    store.setActive(activeId);
    setReplyTo(null); setSelected(null);
    document.body.classList.toggle('chat-thread-open', !!activeId);
    if (activeId) store.loadMessages(activeId).then(() => {
      requestAnimationFrame(() => { const el = scroller.current; if (el) el.scrollTop = el.scrollHeight; });
    }).catch(() => { toast.error(t('error')); setParams({}); });
  }, [activeId]);

  // stick to the bottom when new messages arrive and we were already there; mark them read
  const lastMsgId = list && list.length ? list[list.length - 1].id : 0;
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || !list) return;
    const mineLast = list.length && list[list.length - 1].sender_id === me.id;
    if (atBottom || mineLast) el.scrollTop = el.scrollHeight;
  }, [lastMsgId]);

  useEffect(() => {
    if (activeId && atBottom && document.visibilityState === 'visible' && unread[activeId]) store.markRead(activeId);
  }, [activeId, lastMsgId, atBottom, unread[activeId]]);

  const onScroll = useCallback(async (e) => {
    const el = e.currentTarget;
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
    if (el.scrollTop < 60 && activeId && hasMore[activeId] && !loadingOlder.current && list?.length) {
      loadingOlder.current = true;
      const prevH = el.scrollHeight;
      try { await store.loadMessages(activeId, list[0].id); } catch (err) { /* ignore */ }
      requestAnimationFrame(() => { el.scrollTop = el.scrollHeight - prevH + el.scrollTop; loadingOlder.current = false; });
    }
  }, [activeId, hasMore, list, store]);

  const open = (id) => setParams(id ? { c: String(id) } : {});
  const jump = (id) => {
    const el = document.getElementById(`msg-${id}`);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('flash'); setTimeout(() => el.classList.remove('flash'), 1400); }
  };
  const del = async (m) => {
    if (!window.confirm(t('confirm_delete_message'))) return;
    try { await chatAPI.remove(m.id); store.markDeleted([m.id]); } catch (e) { toast.error(e.response?.data?.detail || t('error')); }
  };
  const askNotif = async () => {
    try { const p = await Notification.requestPermission(); setNotifPerm(p); if (p === 'granted') toast.success(t('notifications_on')); } catch (e) { /* ignore */ }
  };

  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return f ? conversations.filter(c => c.title.toLowerCase().includes(f) || c.members.some(m => m.full_name.toLowerCase().includes(f))) : conversations;
  }, [conversations, filter]);

  const typingNow = conv ? (typing[conv.id] || []) : [];
  const subtitle = conv && (typingNow.length
    ? <span className="typing-line">{conv.is_group ? typingNow.join('، ') + ' ' : ''}{t('typing')}<i /><i /><i /></span>
    : conv.is_group ? `${conv.members.length} ${t('members')}`
    : t('role_' + (conv.members.find(m => m.id !== me.id)?.role || '')));

  return (
    <div className={`chat-shell ${activeId ? 'has-active' : ''}`}>
      {/* ---------- conversation list */}
      <aside className="chat-list">
        <div className="chat-list-head">
          <h1>{t('chat')}</h1>
          <div className="chat-list-tools">
            {notifPerm === 'default' && <button className="btn btn-ghost btn-icon" onClick={askNotif} title={t('enable_notifications')}><Bell size={17} /></button>}
            <button className="chat-new" onClick={() => setShowNew(true)}><Plus size={16} /> {t('new_chat')}</button>
          </div>
        </div>
        <div className="chat-search">
          <Search size={14} />
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('chat_search')} />
        </div>
        <div className="chat-convs">
          {!loaded ? <div className="loading-center"><div className="spinner" /></div>
            : shown.length === 0 ? (
              <div className="chat-list-empty">
                <MessageCircle size={28} />
                <p>{t('no_conversations')}</p>
                <button className="btn btn-primary" onClick={() => setShowNew(true)}><Plus size={15} /> {t('new_chat')}</button>
              </div>
            ) : shown.map(c => {
              const other = c.members.find(m => m.id !== me.id);
              const tp = typing[c.id] || [];
              const n = unread[c.id] || 0;
              return (
                <button key={c.id} className={`chat-conv ${c.id === activeId ? 'active' : ''} ${n ? 'has-unread' : ''}`} onClick={() => open(c.id)}>
                  <Avatar id={c.is_group ? 'g' + c.id : other?.id} name={c.title} group={c.is_group} size={44} />
                  <span className="chat-conv-main">
                    <span className="chat-conv-top">
                      <b dir="auto">{c.title}</b>
                      {c.last_message && <time>{sameDay(c.last_message.created_at, new Date()) ? loc.time(c.last_message.created_at) : loc.short(c.last_message.created_at)}</time>}
                    </span>
                    <span className="chat-conv-bottom">
                      {tp.length ? <em className="typing-line">{t('typing')}<i /><i /><i /></em>
                        : <span dir="auto">{preview(t, c.last_message, me.id) || ' '}</span>}
                      {n > 0 && <span className="chat-badge">{n > 99 ? '99+' : n}</span>}
                    </span>
                  </span>
                </button>
              );
            })}
        </div>
      </aside>

      {/* ---------- thread */}
      <section className="chat-thread">
        {!conv ? (
          <div className="chat-placeholder">
            <svg viewBox="0 0 120 90" aria-hidden="true" className="chat-ph-art">
              <rect x="8" y="10" width="64" height="34" rx="12" className="b1" />
              <rect x="44" y="40" width="68" height="34" rx="12" className="b2" />
              <g className="wv">{[0, 1, 2, 3, 4, 5, 6].map(i => <rect key={i} x={56 + i * 7} y={57 - [4, 9, 6, 11, 5, 8, 3][i]} width="3" height={[4, 9, 6, 11, 5, 8, 3][i] * 2} rx="1.5" />)}</g>
            </svg>
            <p>{t('choose_conversation')}</p>
          </div>
        ) : (
          <>
            <header className="chat-thread-head">
              <button className="btn btn-ghost btn-icon chat-back" onClick={() => open(null)}><ArrowLeft size={18} /></button>
              <Avatar id={conv.is_group ? 'g' + conv.id : conv.members.find(m => m.id !== me.id)?.id} name={conv.title} group={conv.is_group} size={38} />
              <div className="chat-thread-title">
                <b dir="auto">{conv.title}</b>
                <small>{subtitle}</small>
              </div>
              {conv.is_group && <button className="btn btn-ghost btn-icon" onClick={() => setShowGroup(true)} title={t('group_info')}><Settings2 size={17} /></button>}
            </header>
            <div className="chat-scroll" ref={scroller} onScroll={onScroll} onClick={() => setSelected(null)}>
              {list === null ? <div className="loading-center"><div className="spinner" /></div> : (
                <>
                  {hasMore[activeId] && <div className="chat-older"><div className="spinner" /></div>}
                  {list.length === 0 && <div className="chat-first">{t('say_hello')}</div>}
                  {list.map((m, i) => {
                    const prev = list[i - 1];
                    const newDay = !prev || !sameDay(prev.created_at, m.created_at);
                    const showName = newDay || !prev || prev.sender_id !== m.sender_id;
                    return (
                      <React.Fragment key={m.id}>
                        {newDay && <div className="chat-day"><span>{sameDay(m.created_at, new Date()) ? t('today') : loc.day(m.created_at)}</span></div>}
                        <Bubble t={t} m={m} mine={m.sender_id === me.id} showName={showName} isGroup={conv.is_group}
                          readUpto={readUpto[conv.id] || 0} canDelete={m.sender_id === me.id || useAuthStore.getState().can('users.manage')}
                          onReply={setReplyTo} onDelete={del} onJump={jump} onImage={setLightbox} loc={loc}
                          selected={selected === m.id} onSelect={(id) => setSelected(s => (s === id ? null : id))} />
                      </React.Fragment>
                    );
                  })}
                </>
              )}
            </div>
            {!atBottom && (
              <button className="chat-to-bottom" onClick={() => { const el = scroller.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); }}>
                <ArrowDown size={16} />{unread[activeId] > 0 && <span className="chat-badge">{unread[activeId]}</span>}
              </button>
            )}
            <Composer t={t} conv={conv} replyTo={replyTo} setReplyTo={setReplyTo}
              onSent={(m) => { store.addMessages([m]); setAtBottom(true); }} />
          </>
        )}
      </section>

      {showNew && <NewChatModal t={t} onClose={() => setShowNew(false)}
        onCreated={(c) => { store.upsertConversation(c); setShowNew(false); open(c.id); }} />}
      {showGroup && conv && <GroupModal t={t} conv={conv} me={me} onClose={() => setShowGroup(false)}
        onSaved={(c) => { store.upsertConversation(c); setShowGroup(false); }}
        onLeft={(id) => { store.removeConversation(id); setShowGroup(false); open(null); }} />}
      {lightbox && (
        <div className="chat-lightbox" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" />
          <a href={lightbox} download className="btn btn-secondary" onClick={e => e.stopPropagation()}><Download size={15} /> {t('download')}</a>
        </div>
      )}
    </div>
  );
}

